import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { useGlobalConnect, intentIcons } from "@/hooks/useGlobalConnect";
import ProfileBalloon from "@/components/pop-balloon/ProfileBalloon";
import ActionBar from "@/components/pop-balloon/ActionBar";
import PhotosModal from "@/components/pop-balloon/PhotosModal";
import ProfileModal from "@/components/pop-balloon/ProfileModal";
import VideoModal from "@/components/pop-balloon/VideoModal";
import MatchModal from "@/components/pop-balloon/MatchModal";
import { GlobalConnectLimitsBar } from "@/components/global-connect/GlobalConnectLimitsBar";
import { GlobalConnectMatchBadge } from "@/components/global-connect/GlobalConnectMatchBadge";
import { toast } from "sonner";

interface DiscoveryProfile {
  id: string;
  name: string;
  age: number | null;
  bio: string | null;
  photos: string[];
  hasVideo: boolean;
  videoUrl: string | null;
  isVerified: boolean;
  badges: string[];
  distance: string;
  cityName?: string;
  intent?: string;
}

const GlobalConnectDiscovery = () => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { settings, limits, startSession, endSession, useAction } = useGlobalConnect();

  const [country, setCountry] = useState<{ name: string; flag: string } | null>(null);
  const [profiles, setProfiles] = useState<DiscoveryProfile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPopping, setIsPopping] = useState(false);

  // Modal states
  const [showPhotos, setShowPhotos] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [matchedProfile, setMatchedProfile] = useState<DiscoveryProfile | null>(null);

  const currentProfile = profiles[currentIndex];

  // Fetch country info and profiles
  useEffect(() => {
    const init = async () => {
      if (!countryCode || !user) return;

      // Fetch country details
      const { data: countryData } = await supabase
        .from("countries")
        .select("name, flag")
        .eq("code", countryCode.toUpperCase())
        .single();

      if (countryData) {
        setCountry(countryData);
      }

      // Start session
      await startSession(countryCode.toUpperCase());

      // Fetch profiles from this country
      await fetchProfiles();
    };

    init();

    return () => {
      endSession();
    };
  }, [countryCode, user]);

  const fetchProfiles = async () => {
    if (!countryCode || !user || !settings) return;

    setLoading(true);

    try {
      // Get user's past likes/pops to filter out
      const [{ data: likedIds }, { data: poppedIds }] = await Promise.all([
        supabase.from("likes").select("liked_id").eq("liker_id", user.id),
        supabase.from("pops").select("popped_id").eq("popper_id", user.id),
      ]);

      const excludeIds = [
        ...(likedIds?.map((l) => l.liked_id) || []),
        ...(poppedIds?.map((p) => p.popped_id) || []),
      ];

      // Use the optimized RPC function to get eligible profiles
      const { data: eligibleUsers, error: rpcError } = await supabase.rpc(
        "get_global_connect_profiles",
        {
          country_code_param: countryCode.toUpperCase(),
          user_intent: settings.intent_level,
          current_user_id: user.id,
          excluded_user_ids: excludeIds,
        }
      );

      if (rpcError) {
        console.error("Error fetching eligible users:", rpcError);
        setProfiles([]);
        setLoading(false);
        return;
      }

      if (!eligibleUsers || eligibleUsers.length === 0) {
        setProfiles([]);
        setLoading(false);
        return;
      }

      const userIds = eligibleUsers.map((u: { user_id: string }) => u.user_id);
      const intentMap = new Map(
        eligibleUsers.map((u: { user_id: string; intent_level: string }) => [
          u.user_id,
          u.intent_level,
        ])
      );

      // Fetch full profile data
      const { data: profilesData } = await supabase
        .from("profiles")
        .select(`
          *,
          city:cities(name, country_code)
        `)
        .in("id", userIds);

      // Get photos for each profile
      const { data: photosData } = await supabase
        .from("profile_photos")
        .select("*")
        .in("user_id", userIds)
        .order("position");

      const photosByUser: Record<string, string[]> = {};
      photosData?.forEach((photo) => {
        if (!photosByUser[photo.user_id]) {
          photosByUser[photo.user_id] = [];
        }
        const url = supabase.storage
          .from("profile-photos")
          .getPublicUrl(photo.storage_path).data.publicUrl;
        photosByUser[photo.user_id].push(url);
      });

      // Transform to discovery profiles
      const transformedProfiles: DiscoveryProfile[] = (profilesData || []).map((p) => {
        return {
          id: p.id,
          name: p.name,
          age: p.age,
          bio: p.bio,
          photos: photosByUser[p.id] || (p.photo_url ? [p.photo_url] : ["/placeholder.svg"]),
          hasVideo: p.has_intro_video || false,
          videoUrl: p.video_url,
          isVerified: p.is_verified || false,
          badges: p.is_verified ? ["Verified"] : [],
          distance: "Global",
          cityName: (p.city as any)?.name,
          intent: intentMap.get(p.id) as string,
        };
      });

      setProfiles(transformedProfiles);
    } catch (err) {
      console.error("Error in fetchProfiles:", err);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };


  // Actions
  const handleLike = async () => {
    if (!currentProfile || !user || !countryCode) return;

    const canProceed = await useAction("like");
    if (!canProceed) return;

    await supabase.from("likes").insert({
      liker_id: user.id,
      liked_id: currentProfile.id,
      tab_context: "global_connect",
      global_connect_country: countryCode.toUpperCase(),
      global_connect_intent: settings?.intent_level,
    });

    toast.success("Liked! 💕");
    goToNext();
  };

  const handlePop = async () => {
    if (!currentProfile || !user || !countryCode) return;

    const canProceed = await useAction("pop");
    if (!canProceed) return;

    setIsPopping(true);

    // Check for mutual pop
    const { data: mutualPop } = await supabase
      .from("pops")
      .select("id")
      .eq("popper_id", currentProfile.id)
      .eq("popped_id", user.id)
      .maybeSingle();

    await supabase.from("pops").insert({
      popper_id: user.id,
      popped_id: currentProfile.id,
      tab_context: "global_connect",
      global_connect_country: countryCode.toUpperCase(),
      global_connect_intent: settings?.intent_level,
    });

    if (mutualPop) {
      // It's a match!
      await supabase.from("matches").insert({
        user1_id: user.id < currentProfile.id ? user.id : currentProfile.id,
        user2_id: user.id < currentProfile.id ? currentProfile.id : user.id,
        met_context: "global_connect",
        global_connect_country: countryCode.toUpperCase(),
        is_global_match: true,
        match_source: "global_connect",
        source_context: { country_code: countryCode.toUpperCase() },
      });

      await useAction("match");
      setMatchedProfile(currentProfile);
      setShowMatch(true);
    } else {
      toast.success("Popped! 🎈");
    }

    setTimeout(() => {
      setIsPopping(false);
      if (!mutualPop) goToNext();
    }, 500);
  };

  const goToNext = () => {
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      toast.info("You've seen all profiles in this country!");
    }
  };

  const handleReport = async (profileId: string) => {
    toast.success("Report submitted");
    goToNext();
  };

  const handleBlock = async (profileId: string) => {
    if (!user) return;
    await supabase.from("blocked_users").insert({
      blocker_id: user.id,
      blocked_id: profileId,
    });
    toast.success("User blocked");
    goToNext();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/global-connect")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-2xl">{country?.flag}</span>
              <div>
                <h1 className="font-semibold">{country?.name}</h1>
                <p className="text-xs text-muted-foreground">
                  {profiles.length} people • Global Connect
                </p>
              </div>
            </div>
          </div>
          {limits && <GlobalConnectLimitsBar limits={limits} />}
        </div>
      </div>

      {/* Safety Banner */}
      <div className="mx-4 mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
        <div className="flex items-start gap-2">
          <Shield className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            This is a virtual global connection. No travel pressure or financial requests.
          </p>
        </div>
      </div>

      {/* Main Content */}
      {profiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
          <Globe className="w-16 h-16 text-muted-foreground/50 mb-4" />
          <h2 className="text-lg font-semibold mb-2">No profiles yet</h2>
          <p className="text-muted-foreground text-sm mb-4">
            No users with compatible intentions found in this country. Try another!
          </p>
          <Button onClick={() => navigate("/global-connect")}>
            Explore Other Countries
          </Button>
        </div>
      ) : (
        <>
          {/* Profile Balloon */}
          <div className="px-4 py-6">
            {currentProfile && (
              <div className="relative">
                <GlobalConnectMatchBadge
                  countryFlag={country?.flag || "🌍"}
                  countryName={country?.name || ""}
                  intent={currentProfile.intent}
                  className="absolute top-2 left-2 z-10"
                />

                <ProfileBalloon
                  profile={{
                    id: currentProfile.id,
                    name: currentProfile.name,
                    age: currentProfile.age || 0,
                    bio: currentProfile.bio || "",
                    photos: currentProfile.photos,
                    distance: currentProfile.cityName || "Global",
                    isVerified: currentProfile.isVerified,
                    hasVideo: currentProfile.hasVideo,
                    badges: currentProfile.badges,
                  }}
                  onViewPhotos={() => setShowPhotos(true)}
                  onViewProfile={() => setShowProfile(true)}
                  onWatchVideo={() => setShowVideo(true)}
                />
              </div>
            )}
          </div>

          {/* Action Bar */}
          {currentProfile && (
            <div className="px-4">
              <ActionBar
                onLike={handleLike}
                onWatchVideo={() => setShowVideo(true)}
                onPop={handlePop}
                isPopping={isPopping}
              />
            </div>
          )}

          {/* Progress */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>{currentIndex + 1} / {profiles.length}</span>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {currentProfile && (
        <>
          <PhotosModal
            isOpen={showPhotos}
            onClose={() => setShowPhotos(false)}
            profile={{
              id: currentProfile.id,
              name: currentProfile.name,
              age: currentProfile.age || 0,
              bio: currentProfile.bio || "",
              photos: currentProfile.photos,
              distance: currentProfile.cityName || "Global",
              isVerified: currentProfile.isVerified,
              hasVideo: currentProfile.hasVideo,
              badges: currentProfile.badges,
            }}
          />
          <ProfileModal
            isOpen={showProfile}
            onClose={() => setShowProfile(false)}
            profile={{
              id: currentProfile.id,
              name: currentProfile.name,
              age: currentProfile.age || 0,
              bio: currentProfile.bio || "",
              photos: currentProfile.photos,
              isVerified: currentProfile.isVerified,
              hasVideo: currentProfile.hasVideo,
              badges: currentProfile.badges,
              distance: currentProfile.cityName || "Global",
            }}
            onReport={() => handleReport(currentProfile.id)}
            onBlock={() => handleBlock(currentProfile.id)}
          />
          <VideoModal
            isOpen={showVideo}
            onClose={() => setShowVideo(false)}
            profile={{
              id: currentProfile.id,
              name: currentProfile.name,
              age: currentProfile.age || 0,
              bio: currentProfile.bio || "",
              photos: currentProfile.photos,
              distance: currentProfile.cityName || "Global",
              isVerified: currentProfile.isVerified,
              hasVideo: currentProfile.hasVideo,
              badges: currentProfile.badges,
            }}
          />
        </>
      )}

      {matchedProfile && (
        <MatchModal
          isOpen={showMatch}
          onClose={() => {
            setShowMatch(false);
            goToNext();
          }}
          profile={{
            id: matchedProfile.id,
            name: matchedProfile.name,
            age: matchedProfile.age || 0,
            bio: matchedProfile.bio || "",
            photos: matchedProfile.photos,
            distance: "Global",
            isVerified: matchedProfile.isVerified,
            hasVideo: matchedProfile.hasVideo,
            badges: matchedProfile.badges,
          }}
          onViewProfile={() => setShowProfile(true)}
          onStartChat={() => {
            setShowMatch(false);
            navigate("/matches");
          }}
          onWatchVideo={matchedProfile.hasVideo ? () => {
            setShowMatch(false);
            setShowVideo(true);
          } : undefined}
        />
      )}


      {/* BottomNav is rendered in App.tsx */}
    </div>
  );
};

export default GlobalConnectDiscovery;
